import { useMemo, useState } from "react";
import { ApiFactory } from "../../core/config/ApiFactory";
import { useSnackbar } from "../../core/contexts/SnackbarContext";
import { IFiltrosConciliacaoPagamentos, IListagemConciliacaoPagamentos } from "../interfaces/IConciliacaoPagamentos";

const baixarBlob = (blob: Blob, nomeArquivo: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', nomeArquivo);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

export const useConciliacaoPagamentos = () => {
    const [loading, setLoading] = useState(false);
    const [loadingExportacao, setLoadingExportacao] = useState(false);
    const snackbar = useSnackbar();
    const apiFinanceiro = useMemo(() => ApiFactory.getApi("MS_FINANCEIRO"), []);

    const montarQuery = (filtros: IFiltrosConciliacaoPagamentos) => {
        const query = new URLSearchParams();

        Object.entries(filtros).forEach(([chave, valor]) => {
            if (valor) query.append(chave, String(valor));
        });

        return query;
    };

    /**
     * @param incluirTotalizadores manter `false` ao trocar de página: os totais são do período
     * inteiro e não mudam, então recalculá-los a cada página é desperdício.
     */
    const buscar = async (
        filtros: IFiltrosConciliacaoPagamentos,
        paginaAtual = 1,
        incluirTotalizadores = true
    ): Promise<IListagemConciliacaoPagamentos | null> => {
        setLoading(true);
        try {
            const query = montarQuery(filtros);
            query.append('paginaAtual', String(paginaAtual));
            query.append('incluirTotalizadores', String(incluirTotalizadores));

            const { data } = await apiFinanceiro.get(`/contas/conciliacao-pagamentos?${query.toString()}`);
            return data;
        } catch (error: any) {
            snackbar(
                error?.response?.data?.message || "Ocorreu um erro ao buscar os pagamentos",
                { severity: "error" }
            );
            return null;
        } finally {
            setLoading(false);
        }
    };

    const exportar = async (filtros: IFiltrosConciliacaoPagamentos, formato: 'xlsx' | 'csv') => {
        setLoadingExportacao(true);
        try {
            const query = montarQuery(filtros);
            query.append('formato', formato);

            const { data } = await apiFinanceiro.get(
                `/contas/conciliacao-pagamentos/exportar?${query.toString()}`,
                { responseType: 'blob' }
            );

            baixarBlob(data, `conciliacao-pagamentos.${formato}`);
        } catch (error) {
            snackbar("Ocorreu um erro ao exportar o relatório", { severity: "error" });
        } finally {
            setLoadingExportacao(false);
        }
    };

    return { buscar, exportar, loading, loadingExportacao };
};
